
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Market } from "../../markets/marketData";
import { useDatabaseColumns } from "./useDatabaseColumns";

export const useMarketSave = (
  setMarkets: React.Dispatch<React.SetStateAction<Market[]>>, 
  organizationId: string | null,
  dbError: boolean,
  missingColumns: string[] = []
) => {
  const { toast } = useToast();
  const { createMissingColumns } = useDatabaseColumns();

  // Save markets selected in dialog
  const saveMarkets = async (selectedMarkets: Market[]) => {
    if (dbError) {
      // If columns don't exist, try to create them
      const result = await createMissingColumns();
      if (!result.success) {
        toast({
          title: "Erreur",
          description: "Impossible de mettre à jour les marchés. Veuillez contacter l'administrateur.",
          variant: "destructive",
        });
        return;
      }
      
      // If we're in local-only mode due to DB limitations
      if (result.localOnly) {
        // Update local state even though we can't update the database
        setMarkets(selectedMarkets);
        
        toast({
          title: "Mode Simulation",
          description: "Les marchés ont été mis à jour localement uniquement (mode simulation).",
          variant: "default",
        });
        return;
      }
    }
    
    if (!organizationId) {
      toast({
        title: "Erreur",
        description: "Identifiant d'organisation non trouvé.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const defaultMarket = selectedMarkets.find(m => m.isDefault)?.country || selectedMarkets[0].country;
      const enabledMarkets = selectedMarkets.map(m => m.country);
      const activeMarkets = selectedMarkets.filter(m => m.isActive).map(m => m.country);
      
      console.log("Saving markets to organization:", organizationId);
      console.log("Default market:", defaultMarket);
      console.log("Enabled markets:", enabledMarkets);
      console.log("Active markets:", activeMarkets);
      
      // First, try with the update_organization_markets RPC
      try {
        const { error: rpcError } = await supabase.rpc(
          'update_organization_markets',
          { 
            p_organization_id: organizationId,
            p_default_market: defaultMarket,
            p_enabled_markets: enabledMarkets,
            p_active_markets: activeMarkets
          }
        );
        
        if (!rpcError) {
          console.log("Marchés mis à jour avec succès via RPC");
          // Update local state
          setMarkets(selectedMarkets);

          toast({
            title: "Marchés mis à jour",
            description: `Marchés mis à jour avec succès. Marché par défaut: ${defaultMarket}`,
          });
          return;
        }
        
        console.warn("Erreur lors de la mise à jour RPC, tentative de fallback:", rpcError);
      } catch (rpcError) {
        console.error("Exception lors de la mise à jour RPC:", rpcError);
      }
      
      // Fallback to direct update with available columns
      console.log("Tentative de mise à jour directe avec les colonnes disponibles");
      const updateData: any = { 
        updated_at: new Date().toISOString()
      };
      
      // Only include fields for columns that exist
      if (!missingColumns.includes('default_market')) {
        updateData.default_market = defaultMarket;
      }
      
      if (!missingColumns.includes('enabled_markets')) {
        updateData.enabled_markets = enabledMarkets;
      }
      
      if (!missingColumns.includes('active_markets')) {
        updateData.active_markets = activeMarkets;
      }
      
      const { error } = await supabase
        .from('organizations')
        .update(updateData)
        .eq('id', organizationId);
      
      if (error) {
        throw error;
      }
      
      // Update local state
      setMarkets(selectedMarkets);

      const successMessage = missingColumns.length > 0
        ? `Marchés partiellement mis à jour. Certaines données sont disponibles uniquement localement.`
        : `Marchés mis à jour avec succès. Marché par défaut: ${defaultMarket}`;

      toast({
        title: "Marchés mis à jour",
        description: successMessage,
      });
    } catch (error: any) {
      console.error("Erreur lors de la sauvegarde des marchés:", error);
      
      // If error is related to active_markets column not existing
      if (error.code === "42703" && error.message?.includes("active_markets")) {
        console.log("Sauvegarde sans colonne active_markets");
        try {
          const updateData: any = { 
            updated_at: new Date().toISOString()
          };
          
          // Add only fields that we know exist
          const defaultMarket = selectedMarkets.find(m => m.isDefault)?.country || selectedMarkets[0].country;
          const enabledMarkets = selectedMarkets.map(m => m.country);
          
          if (!missingColumns.includes('default_market')) {
            updateData.default_market = defaultMarket;
          }
          
          if (!missingColumns.includes('enabled_markets')) {
            updateData.enabled_markets = enabledMarkets;
          }
          
          const { error: fallbackError } = await supabase
            .from('organizations')
            .update(updateData)
            .eq('id', organizationId);
          
          if (fallbackError) {
            throw fallbackError;
          }
          
          // Update local state even with partial DB update
          setMarkets(selectedMarkets);
          
          toast({
            title: "Mise à jour partielle",
            description: "Les marchés ont été partiellement mis à jour. Certaines fonctionnalités peuvent être limitées.",
            variant: "default",
          });
          return;
        } catch (fallbackError) {
          console.error("Erreur lors de la mise à jour partielle:", fallbackError);
        }
      }
      
      // Last resort - update the local state but inform user of database error
      setMarkets(selectedMarkets);
      
      toast({
        title: "Mode Local Uniquement",
        description: "Les marchés ont été mis à jour en local uniquement. Les changements ne seront pas sauvegardés en base de données.",
        variant: "default",
      });
    }
  };

  return { saveMarkets };
};
